import React, { useState } from 'react';
import {
  Box,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tooltip,
  Badge,
  Chip,
  Typography,
  Popover,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import FunctionsIcon from '@mui/icons-material/Functions';
import SchoolIcon from '@mui/icons-material/School';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import DescriptionIcon from '@mui/icons-material/Description';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';

interface ActionMenuProps {
  onAttachFile: () => void;
  onInsertSymbol: (symbol: string) => void;
  selectedSubject: string;
  instructionMode: string;
  onInstructionModeChange: (mode: string) => void;
  imageCount: number;
  disabled?: boolean;
}

const ActionMenu: React.FC<ActionMenuProps> = ({
  onAttachFile,
  onInsertSymbol,
  selectedSubject,
  instructionMode,
  onInstructionModeChange,
  imageCount,
  disabled = false,
}) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [symbolAnchorEl, setSymbolAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleClose = () => {
    setAnchorEl(null);
    setSymbolAnchorEl(null);
  };

  const handleModeSelect = (mode: string) => {
    onInstructionModeChange(mode);
    setAnchorEl(null);
  };

  const instructionModes = [
    { id: 'default', icon: <InfoOutlinedIcon fontSize="small" />, label: 'Default' },
    { id: 'solution', icon: <SchoolIcon fontSize="small" />, label: 'Step by Step Solution' },
    { id: 'reasoning', icon: <LightbulbIcon fontSize="small" />, label: 'Step by Step Reasoning' },
    { id: 'diagram', icon: <DescriptionIcon fontSize="small" />, label: 'Flow Diagram' },
    { id: 'video', icon: <VideoLibraryIcon fontSize="small" />, label: 'Video Lesson' },
  ];

  const hasActiveMode = instructionMode !== 'default';

  const commonSymbols = [
    'π', '√', '∑', '∫', '∞', '∂', 'Δ', 'θ', 'λ', 'μ',
    'α', 'β', 'γ', 'δ', 'ε', 'φ', 'ω', 'σ', 'τ', 'ψ',
    '×', '÷', '±', '≠', '≤', '≥', '≈', '∝', '°', '∠',
    '∇', '∮', '∏', '∧', '∨', '∩', '∪', '⊂', '⊃', '⊆',
  ];

  const getSubjectSymbols = () => {
    const subject = selectedSubject?.toLowerCase() || '';
    const extra: string[] = [];

    if (subject.includes('physics') || subject.includes('science')) {
      extra.push('ℏ', 'Ω', 'ν', 'µ₀', 'ε₀', 'F⃗', 'E⃗', 'B⃗');
    }
    if (subject.includes('chemistry')) {
      extra.push('→', '⇌', '↓', '↑', '⊕', '⊖', '⁺', '⁻', '²⁺', '³⁺', '²⁻');
    }
    if (subject.includes('math')) {
      extra.push('∀', '∃', '∈', '∉', '⊂', '⊃', '∪', '∩', '∅', 'ℝ', 'ℤ', 'ℚ');
    }

    return extra.length > 0 ? [...commonSymbols, ...extra] : commonSymbols;
  };

  const symbols = getSubjectSymbols();

  return (
    <>
      {/* Plus Button - Inside input, bottom-left corner */}
      <Tooltip title="Attach files & more" arrow>
        <Badge
          badgeContent={imageCount}
          color="primary"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: 9,
              height: 16,
              minWidth: 16,
              padding: '0 3px',
              right: -4,
              top: -4,
            },
          }}
        >
          <IconButton
            onClick={(e) => setAnchorEl(e.currentTarget)}
            disabled={disabled}
            size="small"
            sx={{
              color: hasActiveMode ? '#667eea' : '#888',
              bgcolor: 'transparent',
              borderRadius: '50%',
              width: 32,
              height: 32,
              p: 0.5,
              '&:hover': {
                bgcolor: 'rgba(102,126,234,0.08)',
                transform: 'scale(1.05)',
              },
              transition: 'all 0.2s ease',
            }}
          >
            <AddIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Badge>
      </Tooltip>

      {/* Main Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 260,
            maxHeight: 440,
            borderRadius: 2,
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            mt: -1,
            overflow: 'auto',
            '& .MuiMenuItem-root': {
              py: 1,
              px: 1.5,
              borderRadius: 1,
              mx: 0.5,
              '&:hover': {
                bgcolor: 'rgba(102,126,234,0.06)',
              },
            },
          },
        }}
      >
        {/* Instruction Mode options - directly listed, above Attach image */}
        {instructionModes.map((mode) => (
          <MenuItem
            key={mode.id}
            selected={instructionMode === mode.id}
            onClick={() => handleModeSelect(mode.id)}
            disabled={disabled}
            sx={{
              '&.Mui-selected': {
                bgcolor: 'rgba(102,126,234,0.08)',
                '&:hover': { bgcolor: 'rgba(102,126,234,0.12)' },
              },
            }}
          >
            <ListItemIcon sx={{ minWidth: 36, color: instructionMode === mode.id ? '#667eea' : '#666' }}>
              {mode.icon}
            </ListItemIcon>
            <ListItemText
              primary={mode.label}
              primaryTypographyProps={{
                fontSize: 13,
                fontWeight: instructionMode === mode.id ? 600 : 400,
              }}
            />
            {instructionMode === mode.id && (
              <Box
                sx={{
                  width: 8,
                  height: 8,
                  borderRadius: '50%',
                  bgcolor: '#667eea',
                  flexShrink: 0,
                }}
              />
            )}
          </MenuItem>
        ))}

       <Divider sx={{ my: 0.5, borderColor: 'rgba(0,0,0,0.4)' }} />

        {/* Attach Image */}
        <MenuItem onClick={() => { onAttachFile(); handleClose(); }} disabled={disabled}>
          <ListItemIcon sx={{ minWidth: 36, color: '#667eea' }}>
            <AttachFileIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Attach image"
            secondary={imageCount > 0 ? `${imageCount} image(s) attached` : 'Upload an image file'}
            secondaryTypographyProps={{ fontSize: 10, color: 'text.secondary' }}
          />
          {imageCount > 0 && (
            <Chip
              label={imageCount}
              size="small"
              sx={{ height: 18, fontSize: 9, fontWeight: 600, bgcolor: '#667eea', color: '#fff' }}
            />
          )}
        </MenuItem>

        <Divider sx={{ my: 0.5 }} />

        {/* Insert Symbol */}
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            setSymbolAnchorEl(e.currentTarget);
          }}
          disabled={disabled}
        >
          <ListItemIcon sx={{ minWidth: 36, color: '#667eea' }}>
            <FunctionsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Insert symbol"
            secondary="Math & scientific symbols"
            secondaryTypographyProps={{ fontSize: 10, color: 'text.secondary' }}
          />
          <KeyboardArrowDownIcon fontSize="small" sx={{ color: '#999' }} />
        </MenuItem>
      </Menu>

      {/* Symbol Sub-menu */}
      <Popover
        anchorEl={symbolAnchorEl}
        open={Boolean(symbolAnchorEl)}
        onClose={() => setSymbolAnchorEl(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{
          sx: {
            width: 340,
            maxHeight: 300,
            borderRadius: 2,
            boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
            p: 1.5,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
          {symbols.map((symbol) => (
            <Chip
              key={symbol}
              label={symbol}
              size="small"
              onClick={() => { onInsertSymbol(symbol); setSymbolAnchorEl(null); setAnchorEl(null); }}
              sx={{
                fontSize: 14,
                fontWeight: 500,
                minWidth: 32,
                justifyContent: 'center',
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: '#667eea',
                  color: '#fff',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.15s ease',
              }}
            />
          ))}
        </Box>
        {selectedSubject && (
          <Typography sx={{ fontSize: 10, color: '#999', mt: 1, textAlign: 'center' }}>
            Showing symbols for {selectedSubject}
          </Typography>
        )}
      </Popover>
    </>
  );
};

export default ActionMenu;